import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusApi } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Loader2, Mail, Plus, ArrowLeft, Trash2, Search, X, Tag, PhoneCall, BarChart3, Check,
} from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function CampaignsManager({ open, onOpenChange }) {
  const [view, setView] = useState("list");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const loadList = () => {
    setLoading(true);
    StatusApi.listCampaigns().then(setCampaigns).catch(() => setCampaigns([])).finally(() => setLoading(false));
  };

  useEffect(() => { if (open) { setView("list"); setCreating(false); setNewName(""); loadList(); } }, [open]);

  const createCampaign = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Inserisci un nome per la campagna"); return; }
    try {
      const c = await StatusApi.createCampaign({ name });
      setNewName(""); setCreating(false);
      openDetail(c.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione campagna"); }
  };

  const openDetail = async (id) => {
    try {
      const c = await StatusApi.getCampaign(id);
      setDetail(c); setView("detail");
    } catch { toast.error("Errore apertura campagna"); }
  };

  const removeCampaign = async (id) => {
    try { await StatusApi.deleteCampaign(id); toast.success("Campagna eliminata"); loadList(); }
    catch { toast.error("Errore eliminazione"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0 flex flex-col" data-testid="campaigns-manager">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            {view === "detail" && (
              <button onClick={() => { setView("list"); loadList(); }} data-testid="back-to-list" className="mr-1 text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <Tag className="h-5 w-5 text-primary" />
            {view === "list" ? "Campagne" : detail?.name}
          </DialogTitle>
        </DialogHeader>

        {view === "list" ? (
          <div className="overflow-y-auto px-5 py-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Le campagne sono etichette: crea una campagna e assegnaci le email inviate e le telefonate già fatte, per avere le statistiche da presentare al cliente.
            </p>
            {creating ? (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30" data-testid="new-campaign-form">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Nome campagna *</label>
                <Input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createCampaign()}
                  placeholder="Es. Attico Monza — Luglio 2026" data-testid="campaign-name-input" className="h-9" />
                <div className="flex gap-2">
                  <Button onClick={createCampaign} data-testid="campaign-create-submit" className="h-9">Crea e apri</Button>
                  <Button variant="outline" onClick={() => setCreating(false)} className="h-9">Annulla</Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => setCreating(true)} data-testid="new-campaign-btn" className="w-full h-10">
                <Plus className="h-4 w-4 mr-1.5" /> Nuova campagna
              </Button>
            )}

            {loading ? (
              <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : campaigns.length === 0 ? (
              <p className="py-10 text-center text-muted-foreground text-sm" data-testid="campaigns-empty">Nessuna campagna. Creane una per iniziare.</p>
            ) : campaigns.map((c) => {
              const rate = (n) => (c.emails ? Math.round((n / c.emails) * 100) : 0);
              return (
                <div key={c.id} className="border rounded-lg p-4" data-testid={`campaign-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        creata {fmt(c.created_at)} · {c.calls || 0} telefonate · {c.emails || 0} email
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openDetail(c.id)} data-testid={`open-campaign-${c.id}`} className="h-8">Apri</Button>
                      <button onClick={() => removeCampaign(c.id)} data-testid={`delete-campaign-${c.id}`} className="p-1.5 text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-2 mt-3">
                    <Mini label="Telefonate" n={c.calls} />
                    <Mini label="Email" n={c.emails} />
                    <Mini label="Aperte" n={c.opened} pct={c.emails ? rate(c.opened) : undefined} />
                    <Mini label="Click" n={c.clicked} pct={c.emails ? rate(c.clicked) : undefined} />
                    <Mini label="Richieste" n={c.requested} pct={c.emails ? rate(c.requested) : undefined} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <CampaignDetail campaign={detail} onRefresh={openDetail} onRenamed={loadList} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Mini({ label, n, pct }) {
  return (
    <div className="bg-muted/50 rounded-md p-2 text-center">
      <div className="font-display text-lg font-bold" data-testid={`stat-${label.toLowerCase()}`}>{n || 0}</div>
      <div className="text-[10px] text-muted-foreground">{label}{pct !== undefined ? ` · ${pct}%` : ""}</div>
    </div>
  );
}

const SEND_BADGE = {
  inviata: ["Inviata", "bg-slate-100 text-slate-600"],
  aperta: ["Aperta", "bg-blue-100 text-blue-700"],
  cliccata: ["Cliccata", "bg-emerald-100 text-emerald-700"],
  richiesta: ["Ha richiesto info", "bg-green-600 text-white"],
  cancellato: ["Disiscritto", "bg-red-100 text-red-700"],
};
function SendStatus({ status }) {
  const s = SEND_BADGE[status] || SEND_BADGE.inviata;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${s[1]}`}>{s[0]}</span>;
}

function ItemRow({ it, right }) {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
      <div className="min-w-0 flex items-center gap-2">
        {it.kind === "call"
          ? <PhoneCall className="h-4 w-4 text-emerald-600 shrink-0" />
          : <Mail className="h-4 w-4 text-blue-600 shrink-0" />}
        <div className="min-w-0">
          <div className="font-medium truncate">{it.company_name || it.email || "—"}</div>
          <div className="text-xs text-muted-foreground truncate">
            {it.kind === "call"
              ? `Telefonata · ${fmtDT(it.at)}${it.user_name ? ` · ${it.user_name}` : ""}${it.outcome ? ` · ${it.outcome}` : ""}`
              : `Email · ${it.email || ""} · ${fmtDT(it.at)}`}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {it.kind === "email" && <SendStatus status={it.status} />}
        {right}
      </div>
    </div>
  );
}

function CampaignDetail({ campaign, onRefresh, onRenamed }) {
  const cid = campaign.id;
  const [name, setName] = useState(campaign.name || "");
  const [items, setItems] = useState(campaign.items || []);
  const [stats, setStats] = useState({ calls: campaign.calls, emails: campaign.emails, opened: campaign.opened, clicked: campaign.clicked, requested: campaign.requested });

  // picker (aggiungi attività)
  const [q, setQ] = useState("");
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [feed, setFeed] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [picked, setPicked] = useState(new Set()); // keys "kind:id"

  const refresh = useCallback(async () => {
    try {
      const c = await StatusApi.getCampaign(cid);
      setItems(c.items || []);
      setStats({ calls: c.calls, emails: c.emails, opened: c.opened, clicked: c.clicked, requested: c.requested });
    } catch {}
  }, [cid]);

  const loadFeed = useCallback(async () => {
    setLoadingFeed(true);
    try {
      const r = await StatusApi.activitiesFeed({ q: q || undefined, only_unassigned: onlyUnassigned, days: 120 });
      setFeed(r || []);
    } catch { toast.error("Errore caricamento attività"); }
    finally { setLoadingFeed(false); }
  }, [q, onlyUnassigned]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const saveName = async () => {
    const n = name.trim();
    if (!n) { toast.error("Nome non valido"); return; }
    try { await StatusApi.updateCampaign(cid, { name: n }); toast.success("Nome salvato"); onRenamed && onRenamed(); }
    catch { toast.error("Errore salvataggio nome"); }
  };

  const rate = (n) => (stats.emails ? Math.round((n / stats.emails) * 100) : 0);

  const togglePick = (it) => {
    const key = `${it.kind}:${it.id}`;
    setPicked((prev) => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  };

  const assignPicked = async () => {
    const call_ids = [], send_ids = [];
    picked.forEach((k) => { const [kind, id] = k.split(/:(.+)/); (kind === "call" ? call_ids : send_ids).push(id); });
    if (!call_ids.length && !send_ids.length) return;
    try {
      await StatusApi.assignActivities(cid, { call_ids, send_ids });
      toast.success(`${picked.size} attività aggiunte alla campagna`);
      setPicked(new Set());
      await refresh(); await loadFeed();
    } catch { toast.error("Errore assegnazione"); }
  };

  const unassign = async (it) => {
    const body = it.kind === "call" ? { call_ids: [it.id] } : { send_ids: [it.id] };
    try { await StatusApi.unassignActivities(cid, body); await refresh(); await loadFeed(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="overflow-y-auto px-5 py-4 space-y-5" data-testid="campaign-detail">
      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        <Mini label="Telefonate" n={stats.calls} />
        <Mini label="Email" n={stats.emails} />
        <Mini label="Aperte" n={stats.opened} pct={stats.emails ? rate(stats.opened) : undefined} />
        <Mini label="Click" n={stats.clicked} pct={stats.emails ? rate(stats.clicked) : undefined} />
        <Mini label="Richieste" n={stats.requested} pct={stats.emails ? rate(stats.requested) : undefined} />
      </div>

      {/* Nome */}
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Nome campagna</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="campaign-rename-input" className="h-9 mt-1" />
        </div>
        <Button onClick={saveName} variant="outline" className="h-9" data-testid="save-campaign-name">Salva nome</Button>
      </div>

      {/* Attività nella campagna */}
      <div>
        <div className="flex items-center gap-2 text-sm font-semibold mb-2">
          <BarChart3 className="h-4 w-4 text-primary" /> Attività nella campagna ({items.length})
        </div>
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nessuna attività assegnata. Aggiungile qui sotto.</p>
        ) : (
          <div className="border rounded-lg divide-y max-h-64 overflow-y-auto" data-testid="campaign-items-list">
            {items.map((it) => (
              <div key={`${it.kind}:${it.id}`} data-testid={`campaign-item-${it.kind}-${it.id}`}>
                <ItemRow it={it} right={
                  <button onClick={() => unassign(it)} data-testid={`unassign-${it.kind}-${it.id}`}
                    title="Togli dalla campagna" className="p-1 text-muted-foreground hover:text-red-500"><X className="h-4 w-4" /></button>
                } />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aggiungi attività */}
      <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
        <div className="text-sm font-semibold flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Aggiungi attività alla campagna</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadFeed()}
            placeholder="Cerca per azienda" data-testid="feed-search" className="h-9 flex-1" />
          <Button onClick={loadFeed} disabled={loadingFeed} data-testid="feed-search-btn" variant="outline" className="h-9">
            {loadingFeed ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox checked={onlyUnassigned} onCheckedChange={(v) => setOnlyUnassigned(!!v)} data-testid="feed-only-unassigned" />
          Mostra solo attività non ancora assegnate a una campagna
        </label>

        {loadingFeed ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : feed.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2" data-testid="feed-empty">Nessuna attività trovata negli ultimi 120 giorni.</p>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs">
              <button onClick={() => setPicked(new Set(feed.map((f) => `${f.kind}:${f.id}`)))} className="text-primary hover:underline" data-testid="pick-all-feed">Seleziona tutte ({feed.length})</button>
              <span className="text-muted-foreground">{picked.size} selezionate</span>
            </div>
            <div className="border rounded-md divide-y max-h-72 overflow-y-auto bg-white" data-testid="feed-list">
              {feed.map((it) => {
                const key = `${it.kind}:${it.id}`;
                return (
                  <label key={key} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`feed-item-${it.kind}-${it.id}`}>
                    <Checkbox checked={picked.has(key)} onCheckedChange={() => togglePick(it)} />
                    <div className="flex-1 min-w-0"><ItemRow it={it} right={
                      it.named_campaign_id
                        ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 truncate max-w-[120px]" title={`In: ${it.named_campaign_name}`}>{it.named_campaign_id === cid ? "In questa" : it.named_campaign_name || "assegnata"}</span>
                        : null
                    } /></div>
                  </label>
                );
              })}
            </div>
            <Button onClick={assignPicked} disabled={!picked.size} data-testid="assign-selected-btn" className="h-9">
              <Check className="h-4 w-4 mr-1.5" /> Aggiungi selezionate ({picked.size})
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
